import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check_white.svg';
import { useLocale } from '../../contexts/LocaleContext';
import { getDomainConfig } from '../../lib/domainConfig';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import useHardwareFirstGiftPricing from './useHardwareFirstGiftPricing';
import styles from './GiftSheet.module.scss';

interface HowGiftingWorksSheetProps {
  trigger: JSX.Element;
}

const BENEFIT_KEYS = [
  'hardware_first_gift_sheet_benefit_nothing_to_pay',
  'hardware_first_gift_sheet_benefit_tracking',
  'hardware_first_gift_sheet_benefit_warranty',
];

export default function HowGiftingWorksSheet({ trigger }: HowGiftingWorksSheetProps) {
  const { t } = useTranslation();
  const { baseDomain } = getDomainConfig();
  const { renewalPriceInCents } = useHardwareFirstGiftPricing();
  const formatters = useLocale();

  return (
    <SimpleDismissableModal trigger={trigger} containerClassName={styles.modal} closeIconClassName={styles.closeIcon}>
      {(close) => (
        <div className={styles.container}>
          <span aria-hidden="true" className={styles.handle} />
          <h2 className={styles.title}>{t('hardware_first_gift_sheet_title')}</h2>
          <p className={styles.subtitle}>
            <Trans components={{ bold: <strong /> }} i18nKey="hardware_first_gift_sheet_lede" />
          </p>
          <ul className={styles.checks}>
            {BENEFIT_KEYS.map((key) => (
              <li className={styles.check} key={key}>
                <span className={styles.checkIcon}>
                  <CheckIcon />
                </span>
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
          <dl className={styles.panel}>
            <div className={styles.panelRow}>
              <dt className={styles.label}>{t('hardware_first_gift_sheet_today')}</dt>
              <dd className={styles.value}>{t('hardware_first_gift_sheet_today_value')}</dd>
            </div>
            <div className={styles.panelRow}>
              <dt className={styles.label}>{t('hardware_first_gift_sheet_activation')}</dt>
              <dd className={styles.value}>{t('hardware_first_gift_sheet_activation_value')}</dd>
            </div>
            {renewalPriceInCents !== undefined && (
              <div className={styles.panelRow}>
                <dt className={styles.label}>{t('hardware_first_gift_sheet_after')}</dt>
                <dd className={styles.value}>
                  {t('hardware_first_gift_sheet_after_value', {
                    renewalPrice: formatHardwarePrice(renewalPriceInCents, formatters),
                  })}
                </dd>
              </div>
            )}
          </dl>
          <p className={styles.fine}>{t('hardware_first_gift_sheet_message_fine_print')}</p>
          <p className={styles.fine}>
            <Trans
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                tos: <a href={`https://${baseDomain}/tos`} rel="noopener noreferrer" target="_blank" />,
              }}
              i18nKey="hardware_first_trial_modal_tos"
            />
          </p>
          <button className={styles.cta} onClick={close} type="button">
            {t('hardware_first_trial_modal_got_it')}
          </button>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
